/**
 * DeletePrepModal — the Adviser-only delete confirmation for a preparation
 * request (CLAUDE.md §28.8 / §33.5), mirroring the client-file delete modal.
 * handleDelete stays in PrepRequestDetail.jsx (it is the write path) and is
 * passed in with the setter, so the body is verbatim.
 * Extracted verbatim from PrepRequestDetail.jsx (step P7 of the
 * PrepRequestDetail decomposition, audit §6.2); the moved block keeps its
 * original indentation.
 */

import { motion } from 'framer-motion';

export default function DeletePrepModal({ prep, deleting, setShowDeleteConfirm, handleDelete }) {
  return (
        <div style={styles.modalOverlay} onClick={() => !deleting && setShowDeleteConfirm(false)}>
          <motion.div
            style={styles.modalCard}
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 style={{ ...styles.modalTitle, color: 'var(--cp-danger)' }}>
              {prep.isTest ? 'Delete Test Request' : 'Delete Preparation Request'}
            </h3>
            <p style={styles.modalBody}>
              {prep.isTest
                ? 'Are you sure you want to permanently delete this test preparation request?'
                : 'Are you sure you want to permanently delete this preparation request?'}
            </p>
            <p style={{ fontWeight: 600, fontSize: '0.9rem', marginBottom: '0.75rem', color: 'var(--cp-text)' }}>
              {prep.clientName}
            </p>
            <p style={{ color: 'var(--cp-danger)', fontSize: '0.8rem', marginBottom: '1rem' }}>
              This action cannot be undone. The preparation request and its tasks will be
              permanently removed.
            </p>
            <div style={styles.modalActions}>
              <button className="btn btn-outline" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
                Cancel
              </button>
              <button style={styles.confirmDeleteBtn} onClick={handleDelete} disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete Permanently'}
              </button>
            </div>
          </motion.div>
        </div>
  );
}

// Modal style keys shared with the new-round and confirm-complete modals that
// remain in PrepRequestDetail.jsx (P4 / P6); defined once here, re-attached
// there as references. The import direction is always view -> component.
export const styles = {
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    padding: '1rem',
  },
  modalCard: {
    background: 'white',
    borderRadius: '16px',
    padding: '1.75rem',
    maxWidth: '380px',
    width: '100%',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
  },
  modalTitle: {
    fontSize: '1.1rem',
    fontWeight: 700,
    color: 'var(--cp-text)',
    margin: '0 0 0.5rem',
  },
  modalBody: {
    fontSize: '0.9rem',
    color: 'var(--cp-text-muted)',
    lineHeight: 1.5,
    margin: '0 0 1.25rem',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '0.6rem',
  },
  confirmDeleteBtn: {
    padding: '0.55rem 1rem',
    background: 'var(--cp-danger)',
    color: 'var(--cp-surface)',
    border: 'none',
    borderRadius: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    cursor: 'pointer',
  },
};
